import { Box, Button, FormControl, FormLabel, Paper, Typography } from "@mui/material";
import { FlexContainer } from "AttendanceForm/Components/Display/FlexContainer";
import { FlexBox } from "AttendanceForm/Components/Display/Flexbox";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Form,
  FormField,
  FormFieldError,
  FormFieldLabel,
  FormFieldTextField,
} from "AttendanceForm/Components/FormControls";
import { useAddTrainerDetailsForm } from "AttendanceForm/Forms/TrainerDetails/useAddTrainerDetailsForm";
import { LocationState } from "AttendanceForm/Types/types";
import { ErrorMessage } from "AttendanceForm/Components/Display/ErrorMessage";
import { LoadingIndicator } from "AttendanceForm/Components/LoadingIndicator/LoadingIndicator";
import { useStyles } from "./TrainerDetailsStyles";
import { typographyOverrides } from "AttendanceForm/Theme/Overrides/TypographyOverrides";

interface TrainerDetailsFormProps {
  eventId: string;
  eventHash: string;
}

const firstName = 'firstName'
const lastName = 'lastName'
const email = 'email'

export const TrainerDetailsForm = ({
  eventId,
  eventHash,
}: TrainerDetailsFormProps): React.ReactElement => {
  const { t } = useTranslation("trainerDetailsForm");
  const { t: tcommon } = useTranslation("common");
  const navigate = useNavigate();
  const styles = useStyles();
  const location = useLocation();
  const [eventValidationError, setEventValidationError] = React.useState<string>();
  const { onSubmit, form, loading } = useAddTrainerDetailsForm({
    eventId: eventId,
    eventHash: eventHash,
  });

  const onSubmitCallback = React.useCallback(async () => {
    const response = await onSubmit();
    if (
      !response.isSuccess &&
      form.errors.email === undefined &&
      form.errors.firstName === undefined &&
      form.errors.lastName === undefined
    ) {
      setEventValidationError(response.message);
    }
    if (response.isSuccess) {
      navigate(`/attendanceform/learnerdetails/${eventId}${location.search}`, {
        state: {
          eventId: (location.state as LocationState)?.eventId,
          trainerEmail: form.values.email,
        },
      });
    }
  }, [
    eventId,
    form.errors.email,
    form.errors.firstName,
    form.errors.lastName,
    form.values.email,
    history,
    location.search,
    location.state,
    onSubmit,
  ]);

  return (
    <div>
      {loading ? (
        <Box display="flex" justifyContent="center" className={styles.loader}>
          <LoadingIndicator size="3rem" color="primary" />
        </Box>
      ) : (
        <Box
          paddingBottom={3}
          paddingTop={15}
          width={"100%"}
          flexDirection="row"
          display="flex"
          justifyContent={"center"}
        >
          <Form form={form}>
            <FormControl component="fieldset" aria-disabled="false" className={styles.fieldSetStyle}>
              <Paper elevation={2} className={styles.paper}>
                <FormLabel component="legend" className={styles.legendStyle}>
                  <Box
                    className={styles.background}
                    flexDirection="row"
                    display="flex"
                    marginBottom={1}
                  >
                    <Box marginLeft={1}>
                      <Typography style={typographyOverrides.subtitle1} gutterBottom>
                        {t("trainerDetails")}
                      </Typography>
                      <Box pb={1}>
                        {tcommon("asteriskIndicateMessage")}
                      </Box>
                    </Box>
                  </Box>
                </FormLabel>
                <Box
                  padding={2}
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent={"space-between"}
                >
                  <Box paddingBottom={4}>
                    <FormField name={firstName}>
                      <FormFieldLabel id={firstName}>
                        {tcommon("firstname")}
                        {tcommon("required")}
                      </FormFieldLabel>
                      <FormFieldTextField required={true} />
                      <FormFieldError />
                    </FormField>
                  </Box>
                  <Box paddingBottom={4}>
                    <FormField name={lastName}>
                      <FormFieldLabel id={lastName}>
                        {tcommon("lastname")}
                        {tcommon("required")}
                      </FormFieldLabel>
                      <FormFieldTextField required={true} />
                      <FormFieldError />
                    </FormField>
                  </Box>
                  <Box paddingBottom={4}>
                    <FormField name={email}>
                      <FormFieldLabel id={email}>
                        {tcommon("email")}
                        {tcommon("required")}
                      </FormFieldLabel>
                      <FormFieldTextField required={true} />
                      <FormFieldError />
                    </FormField>
                  </Box>
                  {eventValidationError && (
                    <Box>
                      <ErrorMessage error={eventValidationError} />
                    </Box>
                  )}
                </Box>
                <Box marginTop={6}>
                  <FlexContainer direction="row">
                    <FlexBox />
                    <Box width={100} paddingBottom={1} paddingRight={1}>
                      <Button
                        onClick={onSubmitCallback}
                        color={"primary"}
                        size="medium"
                        fullWidth
                        disabled={loading}
                        disableRipple
                        variant="contained"
                      >
                        {tcommon("next")}
                      </Button>
                    </Box>
                  </FlexContainer>
                </Box>
              </Paper>
            </FormControl>
          </Form>
        </Box>
      )}
    </div>
  );
};